import React from "react";
import { Product } from "@/types/product";
import { Link } from "react-router-dom";
import { formatStorePrice } from "@/lib/storeCurrency";

type Tr = (table: "products", id: string, field: string, original: string | null | undefined) => string;
interface StorefrontProductGridProps {
  products: Product[];
  /** Stage 3 display-only translation; falls back to the original. */
  tr?: Tr;
  /** Store display currency (store_settings.default_currency). */
  currency?: string | null;
}

export function StorefrontProductGrid({ products, tr, currency }: StorefrontProductGridProps) {
  if (!products || products.length === 0) return null;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-7">
      {products.map((product, index) => (
        <StorefrontProductCard key={product.id} product={product} index={index} tr={tr} currency={currency} />
      ))}
    </div>
  );
}

function StorefrontProductCard({ product, index, tr, currency }: { product: Product; index: number; tr?: Tr; currency?: string | null }) {
  const imageUrl =
    product.image ||
    "https://images.unsplash.com/photo-1472851294608-062f824d29cc?w=400&h=500&fit=crop&q=80";

  return (
    <Link
      to={`/product/${product.id}`}
      className="group block animate-fade-in"
      style={{ animationDelay: `${index * 100}ms`, animationFillMode: "both" }}
    >
      <div
        className="rounded-[18px] overflow-hidden bg-white transition-all duration-500 group-hover:-translate-y-2"
        style={{ boxShadow: "0 10px 30px rgba(0,0,0,0.05)" }}
      >
        {/* Image */}
        <div className="relative overflow-hidden">
          <img
            src={imageUrl}
            alt={product.name}
            className="w-full aspect-[4/5] object-cover transition-transform duration-700 ease-out group-hover:scale-105"
            loading="lazy"
            onError={(e) => {
              (e.target as HTMLImageElement).src =
                "https://images.unsplash.com/photo-1472851294608-062f824d29cc?w=400&h=500&fit=crop&q=80";
            }}
          />
        </div>

        {/* Info */}
        <div className="p-5 space-y-1.5">
          <h3 className="font-medium text-sm text-neutral-800 line-clamp-1" translate="no">
            {tr ? tr("products", product.id, "name", product.name) : product.name}
          </h3>
          <p className="text-base font-bold text-neutral-900">{formatStorePrice(product.price, currency)}</p>
        </div>
      </div>
    </Link>
  );
}
